import { getHttpClient } from '@spcc/core'
import type {
  FetchSalesDashboardRequest,
  FetchComparisonRequest,
  DailySalesData,
  ComparisonData,
  TargetProgressData,
  MonthlyTarget,
  CreateTargetPayload,
  UpdateTargetPayload,
} from '../../domain/types'
import { SALES_DASHBOARD_ENDPOINTS } from '../../domain/constants'
import {
  transformDailySalesResponse,
  transformComparisonResponse,
  transformTargetProgressResponse,
  transformMonthlyTargetsListResponse,
  transformMonthlyTargetResponse,
} from '../../api/transformers'

export function buildParams(request?: FetchSalesDashboardRequest): URLSearchParams {
  const params = new URLSearchParams()
  if (request?.from) params.set('from', request.from)
  if (request?.to) params.set('to', request.to)
  if (request?.factoryIds?.length) params.set('factoryIds', request.factoryIds.join(','))
  if (request?.customerIds?.length) params.set('customerIds', request.customerIds.join(','))
  if (request?.cityIds?.length) params.set('cityIds', request.cityIds.join(','))
  if (request?.variantIds?.length) params.set('variantIds', request.variantIds.join(','))
  if (request?.groupBy) params.set('groupBy', request.groupBy)
  return params
}

export function buildUrl(base: string, params: URLSearchParams): string {
  const qs = params.toString()
  return qs ? `${base}?${qs}` : base
}

export const salesDashboardApi = {
  async getDailySales(request?: FetchSalesDashboardRequest): Promise<DailySalesData> {
    const http = getHttpClient()
    const params = buildParams(request)
    const response = await http.get<{ data: unknown }>(buildUrl(SALES_DASHBOARD_ENDPOINTS.DAILY, params))
    // Strapi wraps response in { data: ... } — extract inner data
    const payload = (response.data as any)?.data ?? response.data
    return transformDailySalesResponse(payload)
  },

  async getComparison(request: FetchComparisonRequest): Promise<ComparisonData> {
    const http = getHttpClient()
    const params = buildParams(request)
    params.set('comparisonType', request.comparisonType)
    // Send pre-computed comparison dates so Strapi doesn't need to compute them
    if (request.prevFrom) params.set('prevFrom', request.prevFrom)
    if (request.prevTo) params.set('prevTo', request.prevTo)
    const response = await http.get<{ data: unknown }>(buildUrl(SALES_DASHBOARD_ENDPOINTS.COMPARE, params))
    const payload = (response.data as any)?.data ?? response.data
    return transformComparisonResponse(payload)
  },

  async getTargetProgress(month: string, factoryIds?: string[]): Promise<TargetProgressData> {
    const http = getHttpClient()
    const params = new URLSearchParams({ month })
    if (factoryIds?.length) params.set('factoryIds', factoryIds.join(','))
    const response = await http.get<{ data: unknown }>(buildUrl(SALES_DASHBOARD_ENDPOINTS.TARGET_PROGRESS, params))
    const payload = (response.data as any)?.data ?? response.data
    return transformTargetProgressResponse(payload)
  },

  async getTargets(month?: string): Promise<{ items: MonthlyTarget[]; total: number }> {
    const http = getHttpClient()
    const params = new URLSearchParams()
    params.set('populate[0]', 'factory')
    params.set('populate[1]', 'variant')
    params.set('sort', 'month:desc')
    params.set('pagination[pageSize]', '100')
    if (month) params.set('filters[month][$eq]', month)
    const response = await http.get<unknown>(buildUrl(SALES_DASHBOARD_ENDPOINTS.TARGETS, params))
    return transformMonthlyTargetsListResponse(response.data)
  },

  async createTarget(payload: CreateTargetPayload): Promise<MonthlyTarget> {
    const http = getHttpClient()
    const response = await http.post<{ data: unknown }>(SALES_DASHBOARD_ENDPOINTS.TARGETS, {
      data: {
        month: payload.month,
        factory: payload.factory,
        ...(payload.variant ? { variant: payload.variant } : {}),
        targetQuantity: payload.targetQuantity,
        notes: payload.notes,
      },
    })
    return transformMonthlyTargetResponse(response.data)
  },

  async updateTarget(documentId: string, payload: UpdateTargetPayload): Promise<MonthlyTarget> {
    const http = getHttpClient()
    const response = await http.put<{ data: unknown }>(`${SALES_DASHBOARD_ENDPOINTS.TARGETS}/${documentId}`, {
      data: payload,
    })
    return transformMonthlyTargetResponse(response.data)
  },

  async deleteTarget(documentId: string): Promise<void> {
    const http = getHttpClient()
    await http.delete(`${SALES_DASHBOARD_ENDPOINTS.TARGETS}/${documentId}`)
  },
}
